import type { ERelationBehaviour } from 'api/meta/client-metaapi';
import type { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import type { IFilterOption } from 'components/inputs/textField/TextFilter';
import type { Nullable } from 'utils/values';

import type { IChange } from './changes/templateChanges';

export interface Template {
  id: string;
  technicalName: string;
  defaultLanguage: string;
  supportedLanguages: string;
  nodes: Node_[];
  modules: Module[];
  dataPrefix: string;
  queries: DataQuery[];
  singleValueQueries: SingleValueQuery[];
  hyperRelations: HyperRelation[];
  projectViews: ProjectView[];
  reports: IReportOption[];
  previousVersion: string;
  nextVersions: string[];

  releaseNotes: null;
  releasedOn: string;
  propertyDependencies: unknown[]; // TODO: verify BE
  relationDependencies: unknown[]; // TODO: verify BE

  changes: IChange[];

  personalPageOverviews: string[];
}

export interface HyperRelation {
  markerNodeId: string;
  startNodeId: string;
  partOneRelationId: string;
  hyperNodeId: string;
  partTwoRelationId: string;
  endNodeId: string;
}

export interface Node_ {
  id: string;
  technicalName: string;
  databaseLabel: string;
  icon: string;
  displayNames: DisplayName[];
  relations: Relation[];
  primaryPropertyId: string;
  properties: Property[];
  x: number;
  y: number;
  z: number;
  displayPanels?: unknown[]; // TODO: verify with BE
}

export interface Module {
  id: string;
  displayNames: DisplayName[];
  technicalName: string;
  nodes: string[];
  relations: string[];
  properties: string[];
  color: string;
}

export interface DisplayName {
  languageCode: string;
  value: string;
}

export interface Relation {
  id: string;
  technicalName: string;
  databaseLabel: string;
  displayNames: DisplayName[];
  reverseDisplayNames: DisplayName[];
  targetNodeId: string;
  behaviour: ERelationBehaviour;
  startToEndMinimumNumberOfRelations: number;
  startToEndMaximumNumberOfRelations: number;
  endToStartMinimumNumberOfRelations: number;
  endToStartMaximumNumberOfRelations: number;
  isDecomposable?: boolean;
}

export interface RelationInformation {
  r1: Node_;
  r2: Node_;
  relation: Relation;
}

export interface Property {
  id: string;

  technicalName: string;
  databaseLabel: string;

  index: number;
  pinned: boolean;

  group?: string | null;

  displayNames: DisplayName[];

  settings: PropertySettings;

  // TODO: Verify with BE
  // valueType: EPropertyTypes;
}

export type PropertySettings =
  | AutoIdPropertySettings
  | RationalPropertySettings
  | TextPropertySettings
  | DatePropertySettings
  | PicklistPropertySettings
  | OwnershipPropertySettings
  | CalculatedPropertySettings
  | IntegerPropertySettings;

export interface AutoIdPropertySettings extends IPropertySettings {
  propertyType: EPropertyTypes.String;
  $type: EPropertySettingTypes.AutoId;
  numberOfDigits: number;
  prefix: string;
}

export interface IntegerPropertySettings extends IPropertySettings {
  $type: EPropertySettingTypes.Integer;
  propertyType: EPropertyTypes.Long;
}

export interface RationalPropertySettings extends IPropertySettings {
  $type: EPropertySettingTypes.Rational;
  propertyType: EPropertyTypes.Double;
  precision?: number;
}

export interface TextPropertySettings extends IPropertySettings {
  $type: EPropertySettingTypes.Text;
  propertyType: EPropertyTypes.String;
  renderAsMarkdown?: boolean;
}

export interface DatePropertySettings extends IPropertySettings {
  $type: EPropertySettingTypes.Date;
  propertyType: EPropertyTypes.Date;
}

export interface PicklistPropertySettings extends IPropertySettings {
  propertyType: EPropertyTypes.Double;
  $type: EPropertySettingTypes.Picklist;
  values?: { [pickListItemKey: number]: PicklistPropertyValue };
}

export interface CalculatedPropertySettings extends IPropertySettings {
  // TODO: determine in query
  propertyType: EPropertyTypes;
  $type: EPropertySettingTypes.Calculated;
  /**
   * A reference to the single value query
   * In the model, this is stored by a relation between this property and the single value query @see TemplateRelationTypes.Property.HasSingleValueQuery
   */
  singleValueQueryId?: string;
}

export interface OwnershipPropertySettings extends IPropertySettings {
  propertyType: EPropertyTypes.String;
  $type: EPropertySettingTypes.Ownership;

  values?: { [key: string]: string[] };
}

export interface OwnershipPropertyValue {
  teamId: string;
  teamName: string;

  roleId: string;
  roleName: string;
}

export interface PicklistPropertyValue {
  value: number;
  index: number;
  valueNames: DisplayName[];
  valueInstructions: DisplayName[];
  isArchived: boolean;
  isDefault: boolean;
}

export interface IPropertySettings {
  propertyType: EPropertyTypes;
  $type: PropertySettingTypes;
}

export interface DataQuery extends IQuery<ReturnNode> {
  $type?: QueryTypes;
  startNode: ReturnNode;
  nodes: ReturnNode[];
  name: string;
  id: string;
  isOverview: boolean;
  valueSources: ValueSource[];
  index: number;
  numberOfRowsDisplayed: number;
  parentMap: { [key: string]: string };
  optionalPathMap: { [key: string]: string };
  mandatoryPathMaps: { [key: string]: string }[];
}

export interface SingleValueQuery {
  $type: 'SemantX.MetaApi.Contract.SingleValueQuery, SemantX.MetaApi.Contract';
  id: string;
  startNode: ValueSourceNode;
  nodes: ValueSourceNode[];
  valueSources: ValueSource[];
  parentMap: { [key: string]: string };
  optionalPathMap: { [key: string]: string };
  mandatoryPathMaps: { [key: string]: string }[];

  /**The ValueSource on the StartNode that returns the result */
  valueSourceToRead: ValueSource;
}

export interface IQuery<T> {
  $type?: QueryTypes;
  id: string;
  startNode: T;
  nodes: T[];
  valueSources: ValueSource[];
  parentMap: { [key: string]: string };
  optionalPathMap: { [key: string]: string };
  mandatoryPathMaps: { [key: string]: string }[];
}

export interface ReturnNode extends ITraverseNode {
  /**The key of the query node  */
  id: string;
  aliasOverride: string | null;
  /**The name of this node, only used to construct aliases */
  nodeName: string | null;
  /**The label of the data node  */
  targetLabel: string;
  /**The key of the template node */
  targetTemplateNodeId: string;
  /**Relation traversals starting at this node */
  relations: ITraverseRelation[];
  /**Property traversals starting at this node */
  properties: ITraverseProperty[];
  /**The node condition (optional) */
  condition: Nullable<Condition>;
  /**The parent relation node, this is only to be used by the query engine and will normally be empty */
  parentRelation: Nullable<ITraverseRelation>;
}

export interface ValueSourceNode extends ITraverseNode {
  id: string;
  aliasOverride: string | null;
  nodeName: string;
  targetTemplateNodeId: string;
  targetLabel: string;
  relations: ITraverseRelation[];
  properties: ITraverseProperty[];
  condition: Condition;
  parentRelation: ITraverseRelation;

  valueSources: ValueSourceBase[];
}

export const isValueSourceNode = (node: ITraverseNode | undefined): node is ValueSourceNode => {
  return (node as ValueSourceNode)?.valueSources !== undefined;
};

export interface ITraverseNode extends IHaveId {
  /**If this is set, the alias for this node is determined by another node */
  aliasOverride: string | null;
  targetLabel: string;
  relations: ITraverseRelation[];
  properties: ITraverseProperty[];
  condition?: Nullable<Condition>;
  parentRelation: Nullable<ITraverseRelation>;
  targetTemplateNodeId: string;
  nodeName: string | null;
  $type?: ReturnNodeTypes;
}

export interface IHaveId {
  id: string;
}

export interface ITraverseRelation extends IHaveId {
  $type?: ReturnRelationTypes;
  targetRelationId: string;
  targetRelationLabel: string;
  nextNode: ITraverseNode;
  nextNodeId: string;
  isReverse: boolean;
  canHaveMoreThanOneEndNode: boolean;
  canHaveMoreThanOneStartNode: boolean;
  hyperRelationType: number;
  parentNode: ITraverseNode | null;
  index: number;
}

interface BaseType {
  $type: string;
  id: string | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface ValueSourceBase extends BaseType {
  $type: ValueSourceTypes;
}
export interface ConditionBase extends BaseType {
  $type: ConditionTypes;
}

export interface ITraverseProperty extends IHaveId {
  $type?: string;
  targetPropertyId: string;
  targetPropertyName: string;
  orderIndex: number;
  orderDescending: boolean;
  parentNode: ITraverseNode | null;
  propertyType: EPropertyTypes;
}

export enum EPropertyTypes {
  // Invalid = 0

  // the query engine will use the following property types to determine if the query is valid {
  Long = 1,
  Double = 20,
  String = 2,
  Boolean = 3,

  // spatial values: https://neo4j.com/docs/cypher-manual/current/syntax/spatial/
  PointWgs84 = 15,
  PointWgs843D = 16,
  PointCartesian = 17,
  Cartesian3D = 18,

  // date, time and duration: https://neo4j.com/docs/cypher-manual/current/syntax/temporal/#cypher-temporal-introduction
  Date = 5,
  Time = 9,
  DateTime = 10,
  Duration = 11
}

export type Condition = ExistsCondition | ComparisonCondition | AndCondition | OrCondition | NotCondition;

export type ValueSource =
  | Condition
  | ValueSourceProperty
  | ReturnProperty
  | CountFunction
  | StaticValueSource
  | MathOperatorFunction;

export enum ConditionTypes {
  EXISTS = 'SemantX.MetaApi.Contract.Queries.ValueSources.Exists.Condition.ExistsCondition, SemantX.MetaApi.Contract.Queries',
  AND = 'SemantX.MetaApi.Contract.Queries.ValueSources.And.AndCondition.AndCondition, SemantX.MetaApi.Contract.Queries',
  OR = 'SemantX.MetaApi.Contract.Queries.ValueSources.Or.OrCondition.OrCondition, SemantX.MetaApi.Contract.Queries',
  NOT = 'SemantX.MetaApi.Contract.Queries.ValueSources.Not.Condition.NotCondition, SemantX.MetaApi.Contract.Queries',
  COMPARISON = 'SemantX.MetaApi.Contract.Queries.ValueSources.Comparison.ComparisonCondition.ComparisonCondition, SemantX.MetaApi.Contract.Queries'
}

export interface ExistsCondition extends ConditionBase {
  id: string | undefined;
  $type: ConditionTypes.EXISTS;
  startingNode: ITraverseNode | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface ComparisonCondition extends ConditionBase {
  id: string | undefined;
  $type: ConditionTypes.COMPARISON;
  operator: string | undefined;
  left: ValueSource | undefined;
  right: ValueSource | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface AndCondition extends ConditionBase {
  id: string | undefined;
  $type: ConditionTypes.AND;
  leftSide: Condition | undefined;
  rightSide: Condition | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface OrCondition extends ConditionBase {
  id: string | undefined;
  $type: ConditionTypes.OR;
  leftSide: Condition | undefined;
  rightSide: Condition | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface NotCondition extends ConditionBase {
  id: string | undefined;
  $type: ConditionTypes.NOT;
  whatNot: Condition | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface ReturnProperty extends ITraverseProperty {
  Id: string;
  TargetPropertyId: string;
  TargetPropertyName: string;
  PropertyType: EPropertyTypes;
  OrderIndex: number;
  OrderDescending: boolean;
  ParentNode: ITraverseNode;
}

export enum ValueSourceTypes {
  VALUE_SOURCE_PROPERTY = 'SemantX.MetaApi.Contract.Queries.ValueSourceProperty, SemantX.MetaApi.Contract.Queries',
  STATIC = 'SemantX.MetaApi.Contract.Queries.ValueSources.Static.StaticValueSource, SemantX.MetaApi.Contract.Queries',
  MATH_OPERATOR = 'SemantX.MetaApi.Contract.Queries.ValueSources.Functions.Math.Operators.MathOperatorFunction, SemantX.MetaApi.Contract.Queries',
  COUNT = 'SemantX.MetaApi.Contract.Queries.ValueSources.Functions.Math.Count.CountFunction, SemantX.MetaApi.Contract.Queries',
  BASIC_OPERATOR = 'SemantX.MetaApi.Contract.Queries.ValueSources.Functions.Math.BasicOperator.BasicOperatorFunction, SemantX.MetaApi.Contract.Queries',
  AGGREGATING = 'SemantX.MetaApi.Contract.Queries.ValueSources.Functions.Math.Aggregations.AggregatingValueSource, SemantX.MetaApi.Contract.Queries'
}

export interface StaticValueSource {
  id: string | undefined;
  $type: ValueSourceTypes.STATIC;
  propertyType: EPropertyTypes | undefined;
  value: any;
}

export interface MathOperatorFunction {
  id: string | undefined;
  $type: ValueSourceTypes.MATH_OPERATOR;
  operator: string | undefined;
  left: ValueSource | undefined;
  right: ValueSource | undefined;
  propertyType: EPropertyTypes | undefined;
}

export interface CountFunction {
  id: string | undefined;
  $type: ValueSourceTypes.COUNT;
  propertyType: EPropertyTypes | undefined;

  /**The Id of the node traversal to count */
  targetNodeTraversalToCount: string | undefined;
}

export interface BasicOperatorFunction {
  id: string | undefined;
  $type: ValueSourceTypes.BASIC_OPERATOR;
  propertyType: EPropertyTypes | undefined;

  targetValueSources: Record<string, string>;
  statement: string | undefined;
}

export interface AggregatingValueSource {
  /**The target property to evaluate */
  targetValueSourceToEvaluate: string | undefined;

  // The type of aggregation
  aggregationType: EAggregatingValueSourceType | undefined;

  // Additional parameters that are needed
  parameters: { [key: string]: any } | undefined;
  id: string | undefined;
  propertyType: EPropertyTypes | undefined;
  $type: ValueSourceTypes.AGGREGATING;
}

export enum EAggregatingValueSourceType {
  AverageNumerical = 0,
  AverageDuration = 1,
  Max = 2,
  Min = 3,
  PercentileCont = 4,
  PercentileDisc = 5,
  StandardDeviation = 6,
  StandardDeviationGroup = 7,
  SumNumerical = 8,
  SumDuration = 9
}

export interface ValueSourceProperty extends ITraverseProperty {
  $type: ConditionTypes | ValueSourceTypes;
  targetPropertyId: string;
  targetPropertyName: string;
  orderIndex: number;
  orderDescending: boolean;
  parentNode: ITraverseNode | null;
  id: string;
  propertyType: EPropertyTypes;
}

export interface ProjectViewInStore {
  id: string;
  index: number;
  displayNames: DisplayName[];
  headers: { [headerId: string]: ProjectViewHeader };
  detailViews: string[];
}

export interface ProjectView {
  id: string;
  index: number;
  displayNames: DisplayName[];
  headers: ProjectViewHeader[];
  detailViews: string[];
}

export interface ProjectViewHeader {
  id: string;
  displayNames: DisplayName[];
  index: number;
  overviews: { item1: number; item2: string }[]; // Item1 is index
  hierarchyRelations: { item1: number; item2: string }[]; // Item1 is index
}

export enum EPropertyEditTypes {
  Text = 'text',
  Date = 'date',
  Number = 'number',
  Picklist = 'picklist',
  Ownership = 'ownership',
  AutoId = 'autoId',
  Unknown = 'unknown'
}

export enum EPropertyNavigatorOptions {
  showNodeName = 'showNodeName',
  showEdgeName = 'showEdgeName',
  showEdgeBehaviour = 'showEdgeBehaviour'
}

export type QueryTypes =
  | 'SemantX.MetaApi.Contract.SingleValueQuery, SemantX.MetaApi.Contract'
  | 'SemantX.MetaApi.Contract.DataQuery, SemantX.MetaApi.Contract';

export type PropertySettingTypes = EPropertySettingTypes;

export type ReturnRelationTypes = 'SemantX.MetaApi.Contract.Queries.ReturnRelation, SemantX.MetaApi.Contract.Queries';
export type ReturnNodeTypes = 'SemantX.MetaApi.Contract.Queries.ReturnNode, SemantX.MetaApi.Contract.Queries';

export interface IReportOption extends IFilterOption {
  id: string;
  name: string;

  reportTemplateFileId: string;
  reportTemplateFileName: string;

  connectedOverviewQueryIds: string[];
}
